import { FC, useEffect, useState, useMemo } from 'react';
import { CommunicationsViewProps } from './communications.interfaces';
import { Grid } from '@mui/material';
import { pages } from '../../../utils/common';
import {
  useCommunicationsTabMenuItems,
  useCommunicationsAvailableRouteIds,
} from './communications.helpers';
import MainTitle from '../../components/mainTitle';
import TabGroup from '../../components/tabGroup';
import { Helmet } from 'react-helmet';
import { CONSTANTS } from '../../../utils/CONSTS';
import NavigationDrawer from '../../components/navigationDrawer/navigationDrawer';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { useRbacState } from 'src/context/rbac.context';
import { useRbacSafeLandingPath } from 'src/hooks/useRbacSafeLandingPath';
import { hasAnySubmoduleReadPermission } from 'src/utils/rbacHelpers';

const CommunicationsView: FC<CommunicationsViewProps> = () => {
  const { translateText } = useTranslation();
  const [selectedTab, setSelectedTab] = useState(0);
  const { communicationSubRouteId } = useParams();
  const navigate = useNavigate();
  const { rbac } = useRbacState();
  const safeLanding = useRbacSafeLandingPath();
  const availableRouteIds = useCommunicationsAvailableRouteIds();
  const tabs = useCommunicationsTabMenuItems(translateText);

  const hasAccess = useMemo(
    () => hasAnySubmoduleReadPermission(rbac, 'communications'),
    [rbac]
  );

  useEffect(() => {
    if (availableRouteIds.length === 0) return;

    const routeId =
      communicationSubRouteId && communicationSubRouteId !== 'undefined'
        ? communicationSubRouteId
        : availableRouteIds[0];

    if (!availableRouteIds.includes(routeId)) {
      navigate(pages.communications.path + '/' + availableRouteIds[0], {
        replace: true,
      });
      return;
    }

    const tabIndex = availableRouteIds.indexOf(routeId);
    setSelectedTab(tabIndex >= 0 ? tabIndex : 0);

    if (!communicationSubRouteId || communicationSubRouteId === 'undefined') {
      navigate(pages.communications.path + '/' + routeId, { replace: true });
    }
  }, [communicationSubRouteId, navigate, availableRouteIds]);

  if (!hasAccess) {
    return <Navigate to={safeLanding} replace />;
  }

  if (availableRouteIds.length === 0) {
    return <Navigate to={safeLanding} replace />;
  }

  const locationChangeHandler = (value: string | number) => {
    const tabIndex = value as number;
    setSelectedTab(tabIndex);
    if (tabIndex >= 0 && tabIndex < availableRouteIds.length) {
      navigate(pages.communications.path + '/' + availableRouteIds[tabIndex], {
        replace: true,
      });
    }
  };

  return (
    <NavigationDrawer
      selectedPage={pages.communications.path}
      window={window ?? null}
    >
      <Helmet>
        <title>{CONSTANTS.title.communications}</title>
      </Helmet>
      <Grid
        container
        sx={{ px: { xs: 0, sm: 0 } }}
        data-testid="communications-view-container"
      >
        <Grid
          item
          xs={12}
          sx={{ pr: { xs: 0, sm: 4 } }}
          data-testid="communications-view-header-container"
        >
          <MainTitle title={translateText('communications.title')}
            subTitle={selectedTab ? tabs[selectedTab].label : ''}
          />
        </Grid>
        <Grid
          item
          xs={12}
          sx={{ px: { xs: 0, sm: 0 } }}
          data-testid="communications-view-tabs-container"
        >
          <TabGroup
            key={selectedTab}
            tabs={tabs}
            initiallySelected={selectedTab}
            onTabChange={(value) => locationChangeHandler(value)}
            dataTestIdPrefix="communications"
            limitHeight={true}
          />
        </Grid>
      </Grid>
    </NavigationDrawer>
  );
};

export default CommunicationsView;
